import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import RupiahInput from "@/components/RupiahInput";
import { formatIDR } from "@/lib/constants";
import { computeWeeksOfYear, fmtShortDate, projectLabel } from "./utils";

export default function NewPettyCashDialog({ open, onOpenChange, defaultProjectId, onCreated, existingRows = [], currentUserId = null }) {
  // 2026-02-27 (PROMPT 16-PETTY-CASH-OFFICE-COST-CENTER-R1): cost center state.
  // `costCenterKey` = string "office:<location_id>" atau "project:<project_id>"
  // supaya satu dropdown bisa memilih dari 2 sumber.
  const [costCenters, setCostCenters] = useState({ offices: [], projects: [] });
  const [costCenterKey, setCostCenterKey] = useState("");
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [weekNo, setWeekNo] = useState(null);
  const [items, setItems] = useState([{ _key: crypto.randomUUID(), name: "", qty: 1, unit: "", unit_price: "", note: "" }]);
  // 149-cw-r179: riwayat satuan yg pernah dipakai → saran autocomplete (datalist).
  const [unitHistory, setUnitHistory] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [balance, setBalance] = useState(null);

  const weeks = useMemo(() => computeWeeksOfYear(reportYear), [reportYear]);
  const selectedWeek = useMemo(() => weeks.find((w) => w.no === weekNo) || null, [weeks, weekNo]);

  // Parse cost center key into type + id + snapshot.
  const selected = useMemo(() => {
    if (!costCenterKey) return null;
    const [t, id] = costCenterKey.split(":");
    if (t === "office") {
      const o = costCenters.offices.find((x) => x.id === id);
      return o ? { type: "office", id, name: o.name, snapshot: o } : null;
    }
    if (t === "project") {
      const p = costCenters.projects.find((x) => x.id === id);
      return p ? { type: "project", id, name: projectLabel(p), snapshot: p } : null;
    }
    return null;
  }, [costCenterKey, costCenters]);

  // Weekly Petty Cash Locking — cegah duplikat pengajuan untuk (cost_center + week + user).
  const duplicateEntry = useMemo(() => {
    if (!selected || !selectedWeek || !currentUserId) return null;
    return (existingRows || []).find((r) => {
      if (r.deleted_at) return false;
      if (r.user_id !== currentUserId) return false;
      // Match cost center: office uses location_id; project uses project_id
      if (selected.type === "office") {
        if (r.cost_center_type !== "office" || r.location_id !== selected.id) return false;
      } else {
        if ((r.cost_center_type || "project") !== "project" || r.project_id !== selected.id) return false;
      }
      if (r.week_label && r.week_label === selectedWeek.label) return true;
      if (r.period_start === selectedWeek.start) return true;
      return false;
    }) || null;
  }, [existingRows, selected, selectedWeek, currentUserId]);

  useEffect(() => {
    if (open) {
      const now = new Date();
      const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const yearNow = now.getFullYear();
      setReportYear(yearNow);
      const wsNow = computeWeeksOfYear(yearNow);
      const current = wsNow.find((w) => todayISO >= w.start && todayISO <= w.end);
      setWeekNo(current ? current.no : (wsNow[0]?.no ?? 1));
      setItems([{ _key: crypto.randomUUID(), name: "", qty: 1, unit: "", unit_price: "", note: "" }]);
      setNote("");
      (async () => {
        try {
          const { data } = await api.get("/petty-cash/cost-centers");
          setCostCenters({ offices: data.offices || [], projects: data.projects || [] });
          // Preselect defaultProjectId if given (backward compat with old prop).
          if (defaultProjectId) {
            const inProjects = (data.projects || []).find((p) => p.id === defaultProjectId);
            if (inProjects) setCostCenterKey(`project:${defaultProjectId}`);
          }
        } catch (e) { setCostCenters({ offices: [], projects: [] }); }
      })();
      (async () => {
        try {
          const { data } = await api.get("/auth/bank-accounts");
          setBankAccounts(data || []);
          setBankAccountId(data?.[0]?.id || "");
        } catch (e) { setBankAccounts([]); setBankAccountId(""); }
      })();
      (async () => {
        try {
          const { data } = await api.get("/petty-cash/balance");
          setBalance(data);
        } catch (e) { setBalance(null); }
      })();
    }
  }, [open, defaultProjectId]);

  // Refetch balance when cost center changes (per-project scope for projects; global for offices).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // Balance API only supports project_id scope; office rows keep global balance.
        const params = (selected?.type === "project") ? { project_id: selected.id } : {};
        const { data } = await api.get("/petty-cash/balance", { params });
        setBalance(data);
      } catch (e) { setBalance(null); }
    })();
  }, [open, selected]);

  const addItem = () => setItems((s) => [...s, { _key: crypto.randomUUID(), name: "", qty: 1, unit: "", unit_price: "", note: "" }]);
  const removeItem = (i) => setItems((s) => s.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => setItems((s) => s.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const total = items.reduce((acc, it) => acc + (parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0) || 0), 0);

  const pcMax = balance?.pc_max ?? null;
  const currentBal = balance?.balance ?? 0;
  const projectedBal = currentBal + total;
  const willExceedLimit = pcMax != null && pcMax > 0 && projectedBal > pcMax + 0.005;
  const remainingHeadroom = pcMax != null && pcMax > 0 ? Math.max(0, pcMax - currentBal) : null;

  // 2026-10-14 (user request): batas maks 3 pengajuan belum selesai.
  // null dari backend = tanpa batas (pemilik perm bypass, mis. BOD).
  const openMax = balance?.unverified_prior_max ?? null;
  const openCount = balance?.unverified_prior_count ?? 0;
  const openLimitReached = openMax != null && openCount >= openMax;

  const submit = async () => {
    if (!selected) { toast.error("Cost center (kantor / project) wajib dipilih"); return; }
    if (!selectedWeek) { toast.error("Minggu wajib dipilih"); return; }
    if (willExceedLimit) {
      toast.error(`Pengajuan akan melampaui batas maksimum Rp ${pcMax.toLocaleString("id-ID")}. Kurangi nominal atau selesaikan realisasi/pengembalian dulu.`);
      return;
    }
    const payloadItems = items
      .filter((it) => (it.name || "").trim())
      .map((it) => ({
        name: it.name.trim(),
        qty: parseFloat(it.qty) || 1,
        unit: (it.unit || "").trim() || null,
        unit_price: parseFloat(it.unit_price) || 0,
        note: it.note?.trim() || null,
      }));
    if (payloadItems.length === 0) { toast.error("Minimal 1 item wajib diisi"); return; }
    setLoading(true);
    try {
      const payload = {
        cost_center_type: selected.type,
        location_id: selected.type === "office" ? selected.id : null,
        project_id: selected.type === "project" ? selected.id : null,
        week_label: selectedWeek.label,
        period_start: selectedWeek.start,
        period_end: selectedWeek.end,
        items: payloadItems,
        note: note.trim() || null,
        bank_account_id: bankAccountId || null,
      };
      await api.post("/petty-cash", payload);
      toast.success("Petty cash diajukan");
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dlg-new-pc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Weekly Petty Cash</DialogTitle>
        </DialogHeader>
        {balance && (
          <div className="border border-slate-300 bg-gradient-to-r from-emerald-50 to-slate-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pc-balance-banner">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Total Ditransfer</div>
              <div className="tabular-nums text-sm font-bold text-slate-800" data-testid="pc-balance-transferred">{formatIDR(balance.total_transferred)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Terpakai (Verified)</div>
              <div className="tabular-nums text-sm font-bold text-rose-700" data-testid="pc-balance-spent">− {formatIDR(balance.total_actual_verified)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold">Saldo Tersedia</div>
              <div className={`tabular-nums text-lg font-bold ${balance.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="pc-balance-available">
                {formatIDR(balance.balance)}
              </div>
            </div>
            <div className="text-[10px] text-slate-500 leading-tight self-end">
              {balance.unverified_actual > 0 && (
                <div className="text-amber-700"><span className="font-semibold">Rp {balance.unverified_actual.toLocaleString("id-ID")}</span> aktual belum lolos Verifikasi 1</div>
              )}
              {balance.pending_actual_fill_count > 0 && (
                <div className="text-amber-700">{balance.pending_actual_fill_count} pengajuan menunggu isi aktual</div>
              )}
              <div className="text-slate-400 text-[9px] mt-0.5">{selected?.type === "project" ? "Scope: project ini" : "Scope: semua"}</div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Lokasi / Cost Center *</label>
              {/* 149-cw-r178: SearchableSelect — ketik nama kantor/project utk filter,
                  tidak perlu scroll panjang. */}
              <SearchableSelect
                testid="btn-pc-select-project"
                value={costCenterKey}
                onChange={setCostCenterKey}
                placeholder="Pilih kantor atau project…"
                searchPlaceholder="Ketik nama kantor / project…"
                emptyText="Tidak ada cost center yang cocok"
                groups={[
                  ...(costCenters.offices.length > 0 ? [{
                    label: "Kantor / Lokasi Internal",
                    options: costCenters.offices.map((o) => ({ value: `office:${o.id}`, label: o.name })),
                  }] : []),
                  ...(costCenters.projects.length > 0 ? [{
                    label: "Project Aktif",
                    options: costCenters.projects.map((p) => ({ value: `project:${p.id}`, label: projectLabel(p) })),
                  }] : []),
                ]}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Tahun</label>
              <SearchableSelect
                testid="select-pc-year"
                value={String(reportYear)}
                onChange={(v) => { setReportYear(Number(v)); setWeekNo(1); }}
                placeholder="Pilih tahun…"
                options={Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => ({ value: String(y), label: String(y) }))}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Minggu (Sen–Min) *</label>
              <SearchableSelect
                testid="select-pc-week"
                value={weekNo ? String(weekNo) : ""}
                onChange={(v) => setWeekNo(Number(v))}
                placeholder="Pilih minggu…"
                searchPlaceholder="Ketik nomor minggu / tanggal…"
                options={weeks.map((w) => ({ value: String(w.no), label: `${w.label} · ${fmtShortDate(w.start)} – ${fmtShortDate(w.end)}` }))}
              />
              {selectedWeek && (
                <div className="mt-1 text-[10px] text-slate-500" data-testid="pc-week-period">
                  Periode: <span className="font-semibold text-slate-700">{fmtShortDate(selectedWeek.start)} – {fmtShortDate(selectedWeek.end)}</span>
                </div>
              )}
            </div>
          </div>

          {balance && balance.has_unverified_prior && (
            <div className={`border-2 px-3 py-3 flex items-start gap-2 ${openLimitReached ? "border-rose-500 bg-rose-50" : "border-amber-400 bg-amber-50"}`} data-testid="pc-unverified-prior-warning">
              <AlertTriangle size={18} className={`shrink-0 mt-0.5 ${openLimitReached ? "text-rose-600" : "text-amber-600"}`} />
              <div className={`text-xs ${openLimitReached ? "text-rose-800" : "text-amber-800"}`}>
                {openLimitReached ? (
                  <>
                    <div className="font-bold uppercase tracking-wider text-[11px]">Batas {openMax} Pengajuan Belum Selesai Tercapai</div>
                    <div className="mt-1">Selesaikan salah satu dulu sebelum membuat pengajuan baru:</div>
                  </>
                ) : (
                  <div className="mt-0.5">
                    Ada <span className="font-bold">{openCount}</span> pengajuan belum selesai
                    {openMax != null && <> dari maksimal <span className="font-bold">{openMax}</span></>}
                    {" "}— Anda masih bisa membuat pengajuan baru{openMax != null && <> (pengajuan ke-{openMax + 1} akan ditolak)</>}:
                  </div>
                )}
                <ul className="mt-1 space-y-0.5 list-disc list-inside">
                  {(balance.unverified_prior_labels || []).map((p) => (
                    <li key={p.id}><span className="font-semibold">{p.week_label}</span> — <span className={`italic ${openLimitReached ? "text-rose-600" : "text-amber-700"}`}>{p.state}</span></li>
                  ))}
                  {balance.unverified_prior_count > (balance.unverified_prior_labels || []).length && (
                    <li className={openLimitReached ? "text-rose-600" : "text-amber-700"}>…dan {balance.unverified_prior_count - (balance.unverified_prior_labels || []).length} lainnya</li>
                  )}
                </ul>
                <div className={`mt-2 text-[10px] ${openLimitReached ? "text-rose-700" : "text-amber-700"}`}>Penyelesaian: isi realisasi lengkap & tunggu Verifikasi 1 oleh FAT, atau minta admin hapus pengajuan yang batal.</div>
              </div>
            </div>
          )}

          {duplicateEntry && (
            <div className="border border-amber-400 bg-amber-50 px-3 py-2.5 flex items-start gap-2" data-testid="pc-duplicate-warning">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <div className="font-semibold">Minggu ini sudah punya pengajuan untuk project yang sama.</div>
                <div className="mt-0.5 text-amber-700">
                  Total sebelumnya: <span className="font-semibold tabular-nums">{formatIDR(duplicateEntry.total || 0)}</span>
                  {duplicateEntry.status && <> · Status: <span className="font-semibold uppercase">{duplicateEntry.status}</span></>}
                </div>
                <div className="mt-1 text-[10px]">Pengajuan baru tetap dapat dibuat — pastikan ini bukan input ganda. Bila salah input, edit atau hapus pengajuan yang sudah ada.</div>
              </div>
            </div>
          )}

          {/* Petty Cash Max-Limit Warning — muncul jika projected balance (saldo + pengajuan)
              akan melampaui batas maksimum staff. Backend juga menolak dengan HTTP 400,
              tapi UI harus warn ahead sebelum user submit. */}
          {pcMax != null && pcMax > 0 && total > 0 && willExceedLimit && (
            <div className="border-2 border-rose-500 bg-rose-50 px-3 py-3 flex items-start gap-2" data-testid="pc-limit-warning">
              <AlertTriangle size={18} className="text-rose-600 shrink-0 mt-0.5" />
              <div className="text-xs text-rose-800 flex-1">
                <div className="font-bold uppercase tracking-wider text-[11px]">Melampaui Batas Maksimum Petty Cash</div>
                <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2 tabular-nums">
                  <div>
                    <div className="text-[9px] uppercase text-rose-500">Saldo Saat Ini</div>
                    <div className="font-semibold text-slate-800">{formatIDR(currentBal)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-rose-500">+ Pengajuan Ini</div>
                    <div className="font-semibold text-slate-800">{formatIDR(total)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-rose-500">= Total Proyeksi</div>
                    <div className="font-bold text-rose-700">{formatIDR(projectedBal)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-rose-500">Batas Maks.</div>
                    <div className="font-bold text-rose-700">{formatIDR(pcMax)}</div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] text-rose-700">
                  Selisih over-limit: <span className="font-bold">{formatIDR(projectedBal - pcMax)}</span>.
                  Kurangi item, selesaikan realisasi yang tertunda, atau lakukan pengembalian kas dulu. Bisa juga minta BOD/HRGA menaikkan batas.
                </div>
              </div>
            </div>
          )}

          {/* Info hijau ketika masih ada headroom — memberi tahu batas & sisa yg bisa diajukan */}
          {pcMax != null && pcMax > 0 && !willExceedLimit && total > 0 && remainingHeadroom != null && (
            <div className="border border-emerald-300 bg-emerald-50/60 px-3 py-2 text-[10px] text-emerald-800 tabular-nums flex items-center gap-2" data-testid="pc-limit-info">
              <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full" />
              <span>
                Batas maksimum saldo Anda: <span className="font-semibold">{formatIDR(pcMax)}</span> · Sisa headroom setelah pengajuan ini:
                <span className="font-semibold ml-1">{formatIDR(pcMax - projectedBal)}</span>
              </span>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Rekening Tujuan Transfer *</label>
            {/* 2026-06-10 (user request): rekening bisa diketik utk pencarian —
                konsisten dgn dropdown lain yg sudah SearchableSelect. */}
            <SearchableSelect
              testid="select-pc-bank"
              value={bankAccountId}
              onChange={setBankAccountId}
              placeholder="Pilih rekening…"
              searchPlaceholder="Ketik nama bank / no rekening…"
              emptyText="Tidak ada rekening yang cocok"
              options={bankAccounts.length === 0
                ? [{ value: "", label: "Anda belum punya rekening — tambahkan di My Profile" }]
                : bankAccounts.map((a, i) => ({
                    value: a.id,
                    label: `${i === 0 ? "★ " : ""}${a.bank_name} — ${a.account_no}`,
                  }))}
            />
            {bankAccounts.length === 0 && (
              <div className="mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle size={10} /> Rekening kosong. Simpan minimal 1 rekening di My Profile → Data Bank & Rekening.
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Items *</label>
              <button
                type="button"
                data-testid="btn-pc-add-item"
                onClick={addItem}
                className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add item
              </button>
            </div>
            <table className="w-full text-xs border border-slate-200">
              <thead className="bg-slate-50 uppercase text-[9px] text-slate-600 tracking-wider">
                <tr>
                  <th className="px-2 py-1 text-left w-1/3">Nama Item / Jasa</th>
                  <th className="px-2 py-1 text-right w-20">Qty</th>
                  <th className="px-2 py-1 text-left w-24">Satuan</th>
                  <th className="px-2 py-1 text-right w-32">Harga Satuan</th>
                  <th className="px-2 py-1 text-right w-32">Subtotal</th>
                  <th className="px-2 py-1 text-left">Catatan</th>
                  <th className="px-2 py-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const sub = (parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0)) || 0;
                  return (
                    <tr key={it._key || i} className="border-t border-slate-100">
                      <td className="px-1 py-1">
                        <Input data-testid={`input-pc-item-name-${i}`} value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} className="rounded-none h-8 text-xs" placeholder="Contoh: BBM survey" />
                      </td>
                      <td className="px-1 py-1">
                        <Input data-testid={`input-pc-item-qty-${i}`} type="number" min="0" step="1" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} className="rounded-none h-8 text-sm text-right tabular-nums w-16" />
                      </td>
                      {/* 149-cw-r179: jenis satuan — ketik manual, saran dari riwayat (datalist). */}
                      <td className="px-1 py-1">
                        <Input
                          data-testid={`input-pc-item-unit-${i}`}
                          list="pc-unit-history"
                          value={it.unit}
                          onChange={(e) => updateItem(i, { unit: e.target.value })}
                          className="rounded-none h-8 text-xs w-24"
                          placeholder="pcs"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <RupiahInput testid={`input-pc-item-price-${i}`} value={it.unit_price} onChange={(v) => updateItem(i, { unit_price: v })} className="h-8 text-xs" placeholder="0" />
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-700">{formatIDR(sub)}</td>
                      <td className="px-1 py-1">
                        <Input data-testid={`input-pc-item-note-${i}`} value={it.note} onChange={(e) => updateItem(i, { note: e.target.value })} className="rounded-none h-8 text-xs" placeholder="Opsional" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        {items.length > 1 && (
                          <button data-testid={`btn-pc-remove-item-${i}`} onClick={() => removeItem(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={4} className="px-2 py-1 text-right uppercase text-[10px] font-semibold tracking-wider">Total</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold text-[#0B1D3A]" data-testid="pc-form-total">{formatIDR(total)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
            {/* 149-cw-r179: datalist saran satuan dari riwayat pengajuan sebelumnya. */}
            <datalist id="pc-unit-history">
              {unitHistory.map((u) => <option key={u} value={u} />)}
            </datalist>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Catatan Tambahan</label>
            <Textarea data-testid="input-pc-note" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none mt-1 text-sm" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Batal</Button>
          <Button data-testid="btn-submit-pc" onClick={submit} disabled={loading || willExceedLimit || openLimitReached} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? "Menyimpan…" : (openLimitReached ? `Batas ${openMax} Pengajuan Tercapai` : (willExceedLimit ? "Melampaui Batas Maks" : "Ajukan"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
